import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import HelpPopover from '../../../../../components/blocks/HelpPopover';
import { SlidePreviewThumbs } from '../../fileUpload/components/HoverPeekImage';
import {
    SLIDE_STATUS,
    blockNameForItem,
    countByStatus,
    filterSlides,
    getSlideStatus,
    hasAmbiguousBlockMatch,
    hasAmbiguousStainMatch,
    networkFilenameForItem,
    originalFilenameForItem,
    recutScanChips,
    sortSlides,
    stainLabel,
    statusLabel,
} from './slideStatus';
import styles from './slides.module.css';
import enhancedStyles from '../CaseBuilderEnhanced.module.css';

export const ENHANCED_THUMB_SIZE = 32;
export const FILTER_THRESHOLD = 8;
export const ROW_HEIGHT = 44;

const STATUS_CHIPS = [SLIDE_STATUS.incomplete, SLIDE_STATUS.complete, SLIDE_STATUS.released];
const EMPTY = '—';

function affectedSet(affectedItemIds) {
    return new Set((affectedItemIds || []).map((id) => String(id)));
}

function cellText(value) {
    return value || EMPTY;
}

function emptyListHint(statusFilter, hasTextFilter) {
    if (hasTextFilter) return 'No slides match this filter.';
    if (statusFilter === SLIDE_STATUS.incomplete) return 'None incomplete — clear the status filter to see all.';
    if (statusFilter === SLIDE_STATUS.complete) return 'None ready — clear the status filter to see all.';
    if (statusFilter === SLIDE_STATUS.released) return 'None released yet — clear the status filter to see all.';
    return 'No slides match.';
}

function inboxCountLabel({ itemsLength, visibleLength, statusFilter, textFilterActive, counts }) {
    if (statusFilter) {
        const statusCount = counts[statusFilter] || 0;
        const label = statusLabel(statusFilter).toLowerCase();
        if (textFilterActive) return `${visibleLength} of ${statusCount} ${label}`;
        return `${visibleLength} ${label}`;
    }
    if (textFilterActive) return `${visibleLength} of ${itemsLength}`;
    return `${itemsLength} slide${itemsLength === 1 ? '' : 's'}`;
}

function Cell({ children, title, muted = false, className = '' }) {
    const text = children == null || children === '' ? EMPTY : children;
    const isEmpty = text === EMPTY;
    return (
        <span
            className={`${styles.colCell} ${isEmpty || muted ? styles.colCellMuted : ''} ${className}`.trim()}
            title={title || (isEmpty ? undefined : String(text))}
        >
            {text}
        </span>
    );
}

function statusClass(status) {
    if (status === SLIDE_STATUS.incomplete) return styles.statusIncomplete;
    if (status === SLIDE_STATUS.complete) return styles.statusReady;
    return styles.statusReleased;
}

function statusTabClass(status) {
    if (status === SLIDE_STATUS.incomplete) return styles.statusTabIncomplete;
    if (status === SLIDE_STATUS.complete) return styles.statusTabReady;
    return styles.statusTabReleased;
}

function rowStatusClass(status) {
    if (status === SLIDE_STATUS.incomplete) return styles.inboxRowIncomplete;
    if (status === SLIDE_STATUS.complete) return styles.inboxRowReady;
    return '';
}

export default function SlideList({
    items = [],
    caseBlocks = [],
    affectedItemIds = [],
    onOpenAssign,
    onAddSlides,
    addDisabled = false,
    batchReleaseCount = 0,
    onBatchRelease = null,
    batchReleasePending = false,
    caseQuarantined = false,
    statusFilter: statusFilterProp = undefined,
    onStatusFilterChange = null,
}) {
    const counts = useMemo(() => countByStatus(items), [items]);
    const [statusFilterInternal, setStatusFilterInternal] = useState(null);
    const statusFilter = statusFilterProp !== undefined ? statusFilterProp : statusFilterInternal;
    const setStatusFilter = (next) => {
        const value = typeof next === 'function' ? next(statusFilter) : next;
        if (onStatusFilterChange) onStatusFilterChange(value);
        else setStatusFilterInternal(value);
    };
    const [filter, setFilter] = useState('');
    const parentRef = useRef(null);
    const collisions = useMemo(() => affectedSet(affectedItemIds), [affectedItemIds]);

    useEffect(() => {
        if (parentRef.current) parentRef.current.scrollTop = 0;
    }, [statusFilter, filter]);

    const listItems = useMemo(() => {
        const filtered = filterSlides(items, {
            status: statusFilter || undefined,
            query: filter,
            caseBlocks,
        });
        return sortSlides(filtered);
    }, [items, statusFilter, filter, caseBlocks]);

    const virtualizer = useVirtualizer({
        count: listItems.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 8,
        useFlushSync: false,
    });

    const empty = !items.length;
    const showFilter = items.length >= FILTER_THRESHOLD;
    const textFilterActive = Boolean(filter.trim());
    const releaseCaseQuarantineTitle = caseQuarantined
        ? 'Case is still quarantined — slides won\'t be fully published until you uncheck Case quarantined in the header.'
        : undefined;

    return (
        <div className={styles.inbox}>
            <div className={styles.inboxHeader}>
                <div className={styles.inboxHeaderLeft}>
                    <h3 className={styles.inboxTitle}>Assign slides</h3>
                    <HelpPopover label="How slides work" title="How slides work">
                        <p>
                            Add slides from staging, a filename list, or this computer. Assign a
                            block (regions copy over) and a stain. Section # and Scan # mark deeper
                            sections and digital rescans when needed.
                        </p>
                        <p>
                            Click a slide to open its editor. When a slide is ready, use
                            Release slide there — or Release N ready slides in the toolbar for a
                            batch. Released slides can be placed back in quarantine from the same
                            editor if you need to change them.
                        </p>
                    </HelpPopover>
                    <span className={styles.inboxCount}>
                        {inboxCountLabel({
                            itemsLength: items.length,
                            visibleLength: listItems.length,
                            statusFilter,
                            textFilterActive,
                            counts,
                        })}
                    </span>
                </div>
                <div className={styles.inboxHeaderActions}>
                    {showFilter && (
                        <input
                            type="search"
                            className={styles.filterInput}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter name, block, or stain…"
                            aria-label="Filter slides by name, block, or stain"
                        />
                    )}
                    {batchReleaseCount > 0 && onBatchRelease && (
                        <button
                            type="button"
                            className={enhancedStyles.buttonPrimary}
                            onClick={onBatchRelease}
                            disabled={batchReleasePending}
                            title={releaseCaseQuarantineTitle}
                        >
                            {batchReleasePending
                                ? 'Releasing…'
                                : `Release ${batchReleaseCount} ready slide${batchReleaseCount === 1 ? '' : 's'}`}
                        </button>
                    )}
                    {onAddSlides && (
                        <button
                            type="button"
                            className={batchReleaseCount > 0
                                ? enhancedStyles.buttonSecondary
                                : enhancedStyles.buttonPrimary}
                            onClick={onAddSlides}
                            disabled={addDisabled}
                        >
                            Add slides
                        </button>
                    )}
                </div>
            </div>

            {!empty && (
                <div className={styles.statusTabs} role="group" aria-label="Filter by slide status">
                    {STATUS_CHIPS.map((chip) => {
                        const pressed = statusFilter === chip;
                        return (
                            <button
                                key={chip}
                                type="button"
                                aria-pressed={pressed}
                                className={`${styles.statusTab} ${statusTabClass(chip)} ${pressed ? styles.statusTabActive : ''}`}
                                onClick={() => setStatusFilter((prev) => (prev === chip ? null : chip))}
                            >
                                {statusLabel(chip)}
                                <span className={styles.statusTabCount}>{counts[chip]}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {empty ? (
                <div className={styles.inboxEmpty}>
                    <p className={styles.emptyTitle}>No slides yet</p>
                    <p>Add slides from staging, a filename list, or this computer.</p>
                </div>
            ) : listItems.length === 0 ? (
                <div className={styles.inboxEmpty}>{emptyListHint(statusFilter, textFilterActive)}</div>
            ) : (
                <div className={styles.inboxTable}>
                    <div className={styles.inboxColHeader}>
                        <span className={styles.colPreview} />
                        <span className={styles.colHead}>Original</span>
                        <span className={styles.colHead}>Network name</span>
                        <span className={styles.colHead}>Block</span>
                        <span className={styles.colHead}>Stain</span>
                        <span className={styles.colHead}>Status</span>
                        <span className={styles.colHead}>Flags</span>
                    </div>
                    <div ref={parentRef} className={styles.inboxScroll}>
                        <div
                            style={{
                                height: virtualizer.getTotalSize(),
                                width: '100%',
                                position: 'relative',
                            }}
                        >
                            {virtualizer.getVirtualItems().map((virtualRow) => {
                                const item = listItems[virtualRow.index];
                                const status = getSlideStatus(item);
                                const collision = collisions.has(String(item._id));
                                const ambiguousBlock = hasAmbiguousBlockMatch(item);
                                const ambiguousStain = hasAmbiguousStainMatch(item);
                                const chips = recutScanChips(item);
                                const original = originalFilenameForItem(item);
                                const network = networkFilenameForItem(item);
                                const block = blockNameForItem(item, caseBlocks);
                                const stain = stainLabel(item);
                                const tooltip = [
                                    original,
                                    network,
                                    block || 'No block',
                                    stain || 'No stain',
                                    statusLabel(status),
                                    chips.join(', '),
                                    collision ? 'Collision (block removed)' : '',
                                    ambiguousBlock ? 'Ambiguous block pattern match' : '',
                                    ambiguousStain ? 'Ambiguous stain pattern match' : '',
                                ].filter(Boolean).join('\n');
                                const rowClass = [
                                    styles.inboxRow,
                                    rowStatusClass(status),
                                    collision ? styles.inboxRowCollision : '',
                                    (ambiguousBlock || ambiguousStain) ? styles.inboxRowAmbiguous : '',
                                ].filter(Boolean).join(' ');

                                return (
                                    <div
                                        key={item._id}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: `${virtualRow.size}px`,
                                            transform: `translateY(${virtualRow.start}px)`,
                                        }}
                                    >
                                        <div
                                            className={rowClass}
                                            role="button"
                                            tabIndex={0}
                                            title={tooltip}
                                            onClick={() => onOpenAssign?.(item)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    onOpenAssign?.(item);
                                                }
                                            }}
                                        >
                                            <div className={styles.colPreview}>
                                                <SlidePreviewThumbs
                                                    itemId={item._id}
                                                    hasLargeImage={!!item.largeImage}
                                                    size={ENHANCED_THUMB_SIZE}
                                                />
                                            </div>
                                            <Cell>{cellText(original)}</Cell>
                                            <Cell muted={!network}>{cellText(network)}</Cell>
                                            <Cell muted={!block}>{cellText(block)}</Cell>
                                            <Cell muted={!stain}>{cellText(stain)}</Cell>
                                            <span className={`${styles.statusCell} ${statusClass(status)}`}>
                                                {statusLabel(status)}
                                                {status === SLIDE_STATUS.released && caseQuarantined && (
                                                    <span
                                                        className={styles.statusWarn}
                                                        title="Not published (case is in quarantine)"
                                                        aria-label="Not published (case is in quarantine)"
                                                    >
                                                        <svg
                                                            width="12"
                                                            height="12"
                                                            viewBox="0 0 16 16"
                                                            aria-hidden="true"
                                                            focusable="false"
                                                        >
                                                            <path
                                                                fill="currentColor"
                                                                d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L6.1 5.995A.905.905 0 0 1 7 5h1zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
                                                            />
                                                        </svg>
                                                    </span>
                                                )}
                                            </span>
                                            <div className={styles.inboxChips}>
                                                {chips.map((chip) => (
                                                    <span key={chip} className={styles.chip}>{chip}</span>
                                                ))}
                                                {collision && (
                                                    <span className={styles.chipWarn}>Collision</span>
                                                )}
                                                {ambiguousBlock && (
                                                    <span className={styles.chipWarn} title="Multiple block patterns matched this filename">Ambig. block</span>
                                                )}
                                                {ambiguousStain && (
                                                    <span className={styles.chipWarn} title="Multiple stain patterns matched this filename">Ambig. stain</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
